import { useEffect, useMemo, useState } from 'react';
import { useWebsiteConfig } from './useWebsiteConfig';

declare global {
  interface Window {
    google?: any;
  }
}

const SCRIPT_ID = 'funddharma-google-maps-script';
const BASE_URL = 'https://maps.googleapis.com/maps/api/js';

export function useGoogleMaps(libraries: string[] = ['places']) {
  const { config } = useWebsiteConfig();
  const apiKey = config?.integrations?.googleMapsApiKey?.trim() || '';
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const librariesParam = useMemo(() => libraries.join(','), [libraries]);

  useEffect(() => {
    if (!apiKey) {
      setLoaded(false);
      return;
    }

    if (window.google?.maps?.places) {
      setLoaded(true);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true));
      existing.addEventListener('error', () => setError('Failed to load Google Maps'));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `${BASE_URL}?key=${encodeURIComponent(apiKey)}&libraries=${encodeURIComponent(librariesParam)}`;
    script.onload = () => setLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);
  }, [apiKey, librariesParam]);

  return { loaded, error, hasApiKey: Boolean(apiKey) };
}
